import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { api } from "@/lib/api";
import { usePrepTime, usePublicSettings } from "@/lib/settings";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Minus, Plus, Trash2, AlertCircle, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { imgUrl } from "@/lib/imageUrl";

const TVA_RATE = 2.6;
const STRIPE_ENABLED = (process.env.REACT_APP_STRIPE_ENABLED || "").toLowerCase() === "true";

export default function Checkout() {
  const { items, removeItem, updateQty, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [restaurantPhone, setRestaurantPhone] = useState("021 802 05 93");
  const [form, setForm] = useState({ firstname: "", lastname: "", phone: "", email: "", marketing_optin: false });
  const [payment, setPayment] = useState("onsite");
  const [pickup, setPickup] = useState("asap");
  const [pickupTime, setPickupTime] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [minOrder, setMinOrder] = useState(0);
  const prepTime = usePrepTime();
  const publicSettings = usePublicSettings();

  // Compute available pickup slots for today (Europe/Zurich), respecting hours + prep time
  const pickupSlots = (() => {
    const hours = publicSettings?.hours || {};
    const DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    // Get "now" in Europe/Zurich
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Zurich", hour12: false,
      weekday: "short", hour: "2-digit", minute: "2-digit",
    }).formatToParts(new Date());
    const dayShort = parts.find(p => p.type === "weekday")?.value.toLowerCase();
    const dayMap = { sun: "sunday", mon: "monday", tue: "tuesday", wed: "wednesday", thu: "thursday", fri: "friday", sat: "saturday" };
    const dayId = dayMap[dayShort] || DAYS[new Date().getDay()];
    const nowMin = parseInt(parts.find(p => p.type === "hour").value) * 60 + parseInt(parts.find(p => p.type === "minute").value);
    const h = hours[dayId];
    if (!h || h.closed) return { lunch: [], dinner: [], anyOpen: false, closed: true, asapAvailable: false };

    const earliest = nowMin + Number(prepTime || 20);
    // Round UP to next 15-min mark
    const rounded = Math.ceil(earliest / 15) * 15;

    const toMin = (str) => {
      if (!str) return null;
      const [hh, mm] = str.split(":").map(Number);
      return hh * 60 + mm;
    };

    const genSlots = (openStr, closeStr) => {
      const openMin = toMin(openStr);
      const closeMin = toMin(closeStr);
      if (openMin == null || closeMin == null) return [];
      const start = Math.max(openMin, rounded);
      const slots = [];
      // Stop 5 minutes before close so kitchen has time
      const lastSlot = closeMin - 5;
      for (let m = Math.ceil(start / 15) * 15; m <= lastSlot; m += 15) {
        const hh = String(Math.floor(m / 60)).padStart(2, "0");
        const mm = String(m % 60).padStart(2, "0");
        slots.push(`${hh}:${mm}`);
      }
      return slots;
    };

    const lunch = genSlots(h.lunch_open, h.lunch_close);
    const dinner = genSlots(h.dinner_open, h.dinner_close);

    // ASAP disponible UNIQUEMENT si :
    // - on est actuellement DANS un créneau de service (pas avant, pas entre deux services)
    // - ET on a encore le temps de préparer avant la fermeture du service en cours
    const inService = (openStr, closeStr) => {
      const o = toMin(openStr);
      const c = toMin(closeStr);
      if (o == null || c == null) return false;
      return nowMin >= o && (nowMin + Number(prepTime || 20)) <= (c - 5);
    };
    const asapAvailable = inService(h.lunch_open, h.lunch_close) || inService(h.dinner_open, h.dinner_close);

    return { lunch, dinner, anyOpen: lunch.length + dinner.length > 0, closed: false, asapAvailable };
  })();

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => {
      setRestaurantPhone(data.phone || "021 802 05 93");
      setMinOrder(Number(data.min_order || 0));
    }).catch(() => {});  }, []);

  // Auto-basculer sur "heure précise" si ASAP indisponible (hors service)
  useEffect(() => {
    if (publicSettings?.hours && !pickupSlots.asapAvailable && pickup === "asap") {
      setPickup("scheduled");
    }
  }, [publicSettings, pickupSlots.asapAvailable, pickup]);

  const finalSubtotal = Math.max(0, subtotal - discount);
  const tvaAmount = Math.round((finalSubtotal * TVA_RATE / (100 + TVA_RATE)) * 100) / 100;

  const applyPromo = async () => {
    if (!promoCode) return;
    try {
      const { data } = await api.post("/promo/validate", {
        code: promoCode, subtotal,
        items: items.map(i => ({ product_id: i.product_id, price: i.price, quantity: i.quantity, addons: i.addons }))
      });
      setDiscount(data.discount);
      toast.success(`Code "${promoCode.toUpperCase()}" appliqué : -${data.discount.toFixed(2)} CHF`);
    } catch (e) {
      setDiscount(0);
      toast.error(e.response?.data?.detail || "Code invalide");
    }
  };

  const submit = async () => {
    if (!form.firstname || !form.phone || !form.email) {
      toast.error("Merci de remplir tous les champs obligatoires");
      return;
    }
    if (items.length === 0) {
      toast.error("Votre panier est vide");
      return;
    }
    if (minOrder > 0 && subtotal < minOrder) {
      toast.error(`Le montant minimum de commande est de ${minOrder.toFixed(2)} CHF`);
      return;
    }
    if (pickup === "scheduled" && !pickupTime) {
      toast.error("Merci de choisir une heure de retrait");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id, name: i.name, price: i.price,
          quantity: i.quantity, addons: i.addons, comment: i.comment,
        })),
        customer: form,
        promo_code: promoCode || null,
        payment_method: payment,
        pickup_time: pickup === "asap" ? "asap" : pickupTime,
        notes: "",
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/orders", payload);
      if (payment === "online" && data.checkout_url) {
        // Redirection vers Stripe Checkout (Twint + Carte)
        clear();
        window.location.href = data.checkout_url;
        return;
      }
      clear();
      navigate(`/confirmation/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la commande");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="checkout-empty" className="pt-32 pb-20 min-h-[60vh] max-w-md mx-auto px-6 text-center">
        <ShoppingBag size={48} className="mx-auto text-muted-foreground mb-6" />
        <h1 className="font-display text-4xl text-foreground mb-3">Panier vide</h1>
        <p className="text-muted-foreground mb-8">Ajoutez des plats pour passer commande.</p>
        <Link to="/commander" className="bg-primary text-white px-6 py-3 rounded-sm inline-block hover:bg-primary/90 transition">Voir le menu</Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="pt-28 pb-20 bg-background">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <h1 className="font-display text-5xl text-foreground mb-2">Votre commande</h1>
        <p className="text-muted-foreground mb-12">À emporter — Rue de la Gare 8, Morges</p>

        <div className="grid lg:grid-cols-5 gap-10">
          {/* Items + form */}
          <div className="lg:col-span-3 space-y-8">
            <div className="bg-white border border-border rounded-sm p-6">
              <h2 className="font-display text-2xl mb-4">Articles</h2>
              <div className="space-y-4">
                {items.map((it) => {
                  const addonsTotal = (it.addons || []).reduce((s, a) => s + a.price, 0);
                  const lineTotal = (it.price + addonsTotal) * it.quantity;
                  return (
                    <div key={it.lineId} data-testid={`cart-item-${it.lineId}`} className="flex gap-4 pb-4 border-b border-border last:border-0">
                      {it.image && <img src={imgUrl(it.image)} alt="" className="w-20 h-20 object-cover rounded-sm" />}
                      <div className="flex-1">
                        <div className="flex justify-between gap-2">
                          <h3 className="font-medium">{it.name}</h3>
                          <span className="text-primary font-medium whitespace-nowrap">{lineTotal.toFixed(2)} CHF</span>
                        </div>
                        {it.addons?.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">+ {it.addons.map((a) => a.name).join(", ")}</p>
                        )}
                        {it.comment && <p className="text-xs text-muted-foreground italic mt-1">« {it.comment} »</p>}
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border border-border rounded-sm">
                            <button data-testid={`qty-minus-${it.lineId}`} onClick={() => updateQty(it.lineId, it.quantity - 1)} className="p-2 hover:bg-secondary"><Minus size={14} /></button>
                            <span className="px-3 text-sm">{it.quantity}</span>
                            <button data-testid={`qty-plus-${it.lineId}`} onClick={() => updateQty(it.lineId, it.quantity + 1)} className="p-2 hover:bg-secondary"><Plus size={14} /></button>
                          </div>
                          <button data-testid={`remove-${it.lineId}`} onClick={() => removeItem(it.lineId)} className="text-muted-foreground hover:text-destructive p-2"><Trash2 size={16} /></button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white border border-border rounded-sm p-6">
              <h2 className="font-display text-2xl mb-4">Vos coordonnées</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Prénom *" testid="input-firstname"><Input value={form.firstname} onChange={(e) => setForm({ ...form, firstname: e.target.value })} className="rounded-sm" data-testid="input-firstname" /></Field>
                <Field label="Nom" testid="input-lastname"><Input value={form.lastname} onChange={(e) => setForm({ ...form, lastname: e.target.value })} className="rounded-sm" data-testid="input-lastname" /></Field>
                <Field label="Téléphone *" testid="input-phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm" data-testid="input-phone" /></Field>
                <Field label="Email *" testid="input-email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm" data-testid="input-email" /></Field>
              </div>
              <label className="flex items-start gap-3 mt-5 cursor-pointer p-3 bg-accent rounded-sm">
                <Checkbox checked={form.marketing_optin} onCheckedChange={(v) => setForm({ ...form, marketing_optin: !!v })} data-testid="marketing-optin" className="mt-0.5" />
                <span className="text-sm text-foreground">
                  <strong>Recevoir nos offres exclusives</strong><br />
                  <span className="text-muted-foreground text-xs">Je souhaite être informé(e) des promotions et nouveautés Hanamiya Ramen par email.</span>
                </span>
              </label>
            </div>

            <div className="bg-white border border-border rounded-sm p-6">
              <h2 className="font-display text-2xl mb-4">Retrait</h2>
              <RadioGroup value={pickup} onValueChange={setPickup} className="space-y-2">
                {pickupSlots.asapAvailable ? (
                  <RadioOption value="asap" label={`Dès que possible (≈ ${prepTime} min)`} testid="pickup-asap" />
                ) : (
                  <div
                    data-testid="pickup-asap-disabled"
                    className="flex items-start gap-3 p-3 border border-border rounded-sm bg-secondary/30 opacity-70 cursor-not-allowed"
                    title="Restaurant fermé ou service en pause"
                  >
                    <div className="mt-1 w-4 h-4 rounded-full border border-muted-foreground/50 bg-white shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground line-through">Dès que possible</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Indisponible — le service reprend plus tard. Choisissez un créneau ci-dessous.
                      </p>
                    </div>
                  </div>
                )}
                <RadioOption value="scheduled" label="Choisir une heure précise" testid="pickup-scheduled" />
              </RadioGroup>

              {pickup === "scheduled" && (
                <div className="mt-4 space-y-4" data-testid="pickup-slots-container">
                  {pickupSlots.closed && (
                    <p className="text-sm text-destructive p-3 bg-destructive/5 border border-destructive/30 rounded-sm" data-testid="pickup-closed-msg">
                      Restaurant fermé aujourd&apos;hui. Sélectionnez « Dès que possible » quand nous rouvrirons.
                    </p>
                  )}
                  {!pickupSlots.closed && !pickupSlots.anyOpen && (
                    <p className="text-sm text-amber-800 p-3 bg-amber-50 border border-amber-300 rounded-sm" data-testid="pickup-no-slot-msg">
                      Plus aucun créneau disponible aujourd&apos;hui. Repassez commande demain ou choisissez « Dès que possible ».
                    </p>
                  )}

                  {pickupSlots.lunch.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Service du midi</p>
                      <div className="flex flex-wrap gap-2" data-testid="pickup-slots-lunch">
                        {pickupSlots.lunch.map(t => (
                          <button
                            key={t}
                            type="button"
                            data-testid={`slot-${t.replace(":","")}`}
                            onClick={() => setPickupTime(t)}
                            className={`min-w-[64px] px-3 py-2 text-sm rounded-sm border transition ${pickupTime === t ? "bg-primary text-white border-primary shadow-sm" : "bg-white border-border hover:border-primary/60 hover:bg-primary/5"}`}
                          >{t}</button>
                        ))}
                      </div>
                    </div>
                  )}

                  {pickupSlots.dinner.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Service du soir</p>
                      <div className="flex flex-wrap gap-2" data-testid="pickup-slots-dinner">
                        {pickupSlots.dinner.map(t => (
                          <button
                            key={t}
                            type="button"
                            data-testid={`slot-${t.replace(":","")}`}
                            onClick={() => setPickupTime(t)}
                            className={`min-w-[64px] px-3 py-2 text-sm rounded-sm border transition ${pickupTime === t ? "bg-primary text-white border-primary shadow-sm" : "bg-white border-border hover:border-primary/60 hover:bg-primary/5"}`}
                          >{t}</button>
                        ))}
                      </div>
                    </div>
                  )}

                  {pickupTime && (
                    <p className="text-sm text-primary font-medium" data-testid="pickup-selected-msg">
                      ✓ Retrait prévu à <strong>{pickupTime}</strong>
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="bg-white border border-border rounded-sm p-6">
              <h2 className="font-display text-2xl mb-4">Paiement</h2>
              {STRIPE_ENABLED ? (
                <RadioGroup value={payment} onValueChange={setPayment} className="space-y-3">
                  <label
                    data-testid="payment-online-option"
                    className={`flex items-start gap-3 p-4 border rounded-sm cursor-pointer transition ${payment === "online" ? "border-primary bg-primary/5" : "border-border hover:border-primary/60"}`}
                  >
                    <RadioGroupItem value="online" data-testid="payment-online-radio" className="mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-medium text-foreground">Payer en ligne maintenant</span>
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-primary text-white rounded-sm">Recommandé</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">Carte bancaire · Twint · Apple/Google Pay via Stripe</p>
                      <p className="text-xs text-muted-foreground mt-1">Redirection vers un paiement sécurisé Stripe. Votre commande est envoyée en cuisine dès validation.</p>
                    </div>
                  </label>

                  <label
                    data-testid="payment-onsite-option"
                    className={`flex items-start gap-3 p-4 border rounded-sm cursor-pointer transition ${payment === "onsite" ? "border-primary bg-primary/5" : "border-border hover:border-primary/60"}`}
                  >
                    <RadioGroupItem value="onsite" data-testid="payment-onsite-radio" className="mt-0.5" />
                    <div className="flex-1">
                      <span className="font-medium text-foreground">Payer sur place au retrait</span>
                      <p className="text-sm text-muted-foreground mt-1">Twint · Espèces · Carte bancaire</p>
                      <p className="text-xs text-muted-foreground mt-1">Aucune carte demandée en ligne.</p>
                    </div>
                  </label>
                </RadioGroup>
              ) : (
                <div data-testid="payment-onsite-only" className="flex items-start gap-3 p-4 bg-accent rounded-sm border border-primary/20">
                  <div className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center shrink-0 font-semibold text-xs">CHF</div>
                  <div>
                    <p className="font-medium text-foreground">Paiement sur place au retrait</p>
                    <p className="text-sm text-muted-foreground mt-1">Twint · Espèces · Carte bancaire</p>
                    <p className="text-xs text-muted-foreground mt-2">Aucune carte n&apos;est demandée en ligne. Le paiement en ligne sera disponible prochainement.</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Summary sidebar */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-border rounded-sm p-6 lg:sticky lg:top-28 space-y-4">
              <h2 className="font-display text-2xl">Récapitulatif</h2>

              <div className="flex gap-2">
                <Input placeholder="Code promo" value={promoCode} onChange={(e) => setPromoCode(e.target.value)} className="rounded-sm" data-testid="promo-input" />
                <button onClick={applyPromo} data-testid="apply-promo-btn" className="px-4 bg-foreground text-white text-sm rounded-sm hover:bg-foreground/90">Appliquer</button>
              </div>

              <div className="border-t border-border pt-4 space-y-2 text-sm">
                <Row label="Sous-total" value={`${subtotal.toFixed(2)} CHF`} />
                {discount > 0 && <Row label="Réduction" value={`-${discount.toFixed(2)} CHF`} className="text-primary" />}
                <Row label={`TVA incluse (${TVA_RATE}%)`} value={`${tvaAmount.toFixed(2)} CHF`} muted />
                <div className="border-t border-border pt-3 flex justify-between text-lg font-semibold">
                  <span>Total</span>
                  <span className="text-primary" data-testid="checkout-total">{finalSubtotal.toFixed(2)} CHF</span>
                </div>
                {minOrder > 0 && subtotal < minOrder && (
                  <div className="mt-3 p-3 bg-destructive/10 border border-destructive text-destructive rounded-sm text-xs" data-testid="min-order-warning">
                    Minimum de commande : <strong>{minOrder.toFixed(2)} CHF</strong>. Il manque {(minOrder - subtotal).toFixed(2)} CHF.
                  </div>
                )}
              </div>

              <div className="flex items-start gap-3 p-4 bg-accent rounded-sm text-xs text-foreground">
                <AlertCircle size={18} className="text-primary shrink-0 mt-0.5" />
                <p>Si la confirmation de votre commande prend plus de 2 minutes, merci de nous appeler au <a href={`tel:${restaurantPhone}`} className="font-semibold underline">{restaurantPhone}</a>.</p>
              </div>

              <button
                onClick={submit}
                disabled={submitting}
                data-testid="submit-order-btn"
                className="w-full bg-primary text-white py-4 rounded-sm hover:bg-primary/90 transition disabled:opacity-60 font-medium"
              >
                {submitting ? "Envoi…" : payment === "online" ? `Payer ${finalSubtotal.toFixed(2)} CHF en ligne` : "Confirmer la commande"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function RadioOption({ value, label, testid, disabled }) {
  return (
    <label className={`flex items-center gap-3 p-3 border border-border rounded-sm cursor-pointer hover:border-primary transition ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
      <RadioGroupItem value={value} disabled={disabled} data-testid={testid} />
      <span className="text-sm">{label}</span>
    </label>
  );
}

function Row({ label, value, muted, className = "" }) {
  return (
    <div className={`flex justify-between ${muted ? "text-muted-foreground" : ""} ${className}`}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}
